import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OwlOptions } from 'ngx-owl-carousel-o';
import { firstValueFrom } from 'rxjs';
import emailjs, { type EmailJSResponseStatus } from '@emailjs/browser';
import { Title, Meta, DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { Business, BusinessService } from '../../../services/business.service';
import { LanguageService } from '../../../services/language.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
@Component({
  selector: 'app-home-five',
  templateUrl: './home-five.component.html',
  styleUrls: ['./home-five.component.css']
})
export class HomeFiveComponent implements OnInit {
  business: Business | null = null;
  error: string | null = null;
  menuUrl = '';
  hasMultipleBranches = false;
  menuBranches: any[] = [];
  language: 'en' | 'ar' = 'en';

  customOptions: OwlOptions = {
    loop: true, dots: true, margin: 0, nav: true, autoplay: true,
    autoplaySpeed: 2000, navSpeed: 2000, items: 1,
    navText: ['<i class="icofont icofont-arrow-left"></i>', '<i class="icofont icofont-arrow-right"></i>'],
    rtl: false
  };

  formData = { user_name: '', user_email: '', message: '' };
  private serviceId = 'contact-form';
  private templateId = 'contact-form-becki';
  private publicKey = 'gObMl0a3V0Jib9k5c';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private businessService: BusinessService,
    private titleService: Title,
    private metaService: Meta,
    public languageService: LanguageService,
    private http: HttpClient,
    private sanitizer: DomSanitizer
  ) {
    const stored = localStorage.getItem('appLanguage');
    this.language = (stored === 'ar' ? 'ar' : 'en') as 'en' | 'ar';
    this.customOptions = { ...this.customOptions, rtl: this.language === 'ar' };

    this.languageService.language$.subscribe(() => {
      this.language = this.languageService.getLanguage() as 'en' | 'ar';
      this.customOptions = { ...this.customOptions, rtl: this.language === 'ar' };
      if (this.business) {
        // Use **only** the name, no "We Are" prefix
        this.titleService.setTitle(this.languageService.getBusinessNameOnly(this.business));
      }
    });
  }

  async ngOnInit(): Promise<void> {
    const businessId = this.route.snapshot.paramMap.get('businessId');
    if (businessId && !isNaN(+businessId)) {
      try {
        const data: any = await firstValueFrom(this.businessService.getLandingPage(+businessId));
        if (data._redirect_to_chain) {
          this.router.navigate([`/${data._redirect_to_chain}`]);
          return;
        }
        this.business = data as Business; // ← 'as Business' is the key fix

        if (this.business.favicon) {
          this.metaService.updateTag({ name: 'icon', content: `${environment.baseUrl}${this.business.favicon}` });
        }
        this.titleService.setTitle(this.languageService.getBusinessNameOnly(this.business));
        this.business.sliders = (this.business.sliders || []).filter(s => s.image_url && s.image_url.trim() !== '');
        const sliderCount = this.business.sliders.length;
        if (sliderCount <= 1) {
          this.customOptions = { ...this.customOptions, loop: false, nav: false, dots: false, autoplay: false, mouseDrag: false, touchDrag: false };
        } else if (sliderCount === 2) {
          this.customOptions = { ...this.customOptions, loop: false };
        }

        try {
          const resolved = await firstValueFrom(
            this.http.get<any>(`${environment.apiUrl}/resolve-menu/${businessId}?type=chain`)
          );
          console.log('businessId param:', businessId);
          console.log('resolved:', resolved);
          console.log('menuBranches will be:', resolved.branches?.map((b: any) => b.id));
          // In home-one.component.ts, replace the resolve-menu block:
          if (resolved.type === 'chain' && resolved.branch_count > 1) {
            this.hasMultipleBranches = true;
            this.menuBranches = (resolved.branches || []).map((b: any) => ({
              id: b.id,
              name: this.language === 'ar' && b.name_ar ? b.name_ar : b.name,
              url: b.custom_domain ? `https://menu.${b.custom_domain}` : (b.subdomain ? `https://menu-${b.subdomain}.qode.qa` : `${environment.redirectUrl}/${b.id}`)
            }));
            this.menuUrl = ''; // no single URL — navbar uses dropdown
          } else {
            this.hasMultipleBranches = false;
            this.menuBranches = [];
            const singleBranch = resolved.type === 'branch' ? resolved : resolved.branches?.[0];
            const singleBranchId = resolved.type === 'branch'
              ? resolved.branch_id
              : resolved.branches?.[0]?.id;
            this.menuUrl = singleBranch?.custom_domain ? `https://menu.${singleBranch.custom_domain}` : (singleBranch?.subdomain ? `https://menu-${singleBranch.subdomain}.qode.qa` : `${environment.redirectUrl}/${singleBranchId || businessId}`);
          }
        } catch (e) {
          this.hasMultipleBranches = false;
          this.menuBranches = [];
          this.menuUrl = `${environment.redirectUrl}/${businessId}`;
        }

      } catch (err: any) {
        this.error = err.message || 'Failed to load business';
        this.router.navigate(['/not-found'], { queryParams: { businessId, error: 'not-found' } });
      }
    } else {
      this.router.navigate(['/home']);
    }
  }

  getLogoUrl(logoPath: string): string {
    return logoPath ? `${environment.baseUrl}${logoPath}` : '/assets/placeholder.png';
  }

  getSliderImageUrl(slider: { image_url?: string }): SafeStyle {
    const src = slider.image_url ? `${environment.baseUrl}${slider.image_url}` : '/assets/placeholder.png';
    return this.sanitizer.bypassSecurityTrustStyle(`url("${src}")`);
  }

  getServiceIconUrl(icon_url: string): string {
    return icon_url ? `${environment.baseUrl}${icon_url}` : '/assets/placeholder.png';
  }

  getServiceColumnClass(serviceCount: number): string {
    return serviceCount <= 2 ? 'col-md-6 col-sm-6 col-xs-12' : 'col-md-4 col-sm-4 col-xs-12';
  }

  public sendEmail(event: Event): void {
    event.preventDefault();
    emailjs.sendForm(this.serviceId, this.templateId, event.target as HTMLFormElement, this.publicKey)
      .then(
        () => {
          alert(this.languageService.getTranslation('sendMessage') + ' successfully. Thank you, we will get back to you soon!');
          this.resetForm();
        },
        (error: EmailJSResponseStatus) => {
          console.error('Failed to send message. Error:', error.text);
          alert('Failed to send the ' + this.languageService.getTranslation('message') + '. Please try again later.');
        }
      );
  }

  private resetForm(): void {
    this.formData = { user_name: '', user_email: '', message: '' };
  }

  getSocialMediaLinks(): { platform: string; link: string; icon: string }[] {
    if (!this.business?.social_urls) return [];
    const socials = this.business.social_urls;
    const platforms = [
      { platform: this.languageService.getTranslation('facebook'), key: 'facebook', icon: 'icofont icofont-social-facebook' },
      { platform: this.languageService.getTranslation('instagram'), key: 'instagram', icon: 'icofont icofont-social-instagram' },
      { platform: this.languageService.getTranslation('behance'), key: 'behance', icon: 'icofont icofont-social-behance' },
      { platform: this.languageService.getTranslation('linkedin'), key: 'linkedin', icon: 'icofont icofont-social-linkedin' },
      { platform: this.languageService.getTranslation('tiktok'), key: 'tiktok', icon: 'fab fa-tiktok' },
      { platform: this.languageService.getTranslation('youtube'), key: 'youtube', icon: 'icofont icofont-social-youtube' },
      { platform: this.languageService.getTranslation('twitter'), key: 'twitter', icon: 'icofont icofont-social-twitter' },
      { platform: this.languageService.getTranslation('whatsapp'), key: 'whatsapp', icon: 'icofont icofont-social-whatsapp' },
      { platform: this.languageService.getTranslation('google_reviews'), key: 'google_reviews', icon: 'fas fa-star' }
    ] as const;
    return platforms
      .filter(p => socials[p.key] && typeof socials[p.key] === 'string')
      .map(p => ({ platform: p.platform, link: socials[p.key] as string, icon: p.icon }));
  }

  getServices(): any[] {
    return this.language === 'ar' && this.business?.services_ar?.length ? this.business.services_ar : this.business?.services || [];
  }

  getDescription(): string {
    return this.language === 'ar' && this.business?.description_ar ? this.business.description_ar : this.business?.description || '';
  }

  getAboutUs(): string {
    return this.language === 'ar' && this.business?.about_us_ar ? this.business.about_us_ar : this.business?.about_us || '';
  }

  getPostalAddress(): string {
    return this.language === 'ar' && this.business?.postal_address_ar ? this.business.postal_address_ar : this.business?.postal_address || '';
  }
}
